import React, { useRef, useState, useImperativeHandle, forwardRef } from 'react';
import { Box, Button, Typography, Stack } from '@mui/material';
import { DeleteOutline, GestureOutlined } from '@mui/icons-material';

const SignatureCanvas = forwardRef(({ onStrokeEnd }, ref) => {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [paths, setPaths] = useState([]);

  useImperativeHandle(ref, () => ({
    clear: () => {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      setPaths([]);
    },
    getSvg: () => {
      if (paths.length === 0) return null;
      let svgPathData = "";
      paths.forEach((path) => {
        if (path.length < 2) return;
        svgPathData += `M ${path[0].x} ${path[0].y} `;
        for (let i = 1; i < path.length; i++) {
          svgPathData += `L ${path[i].x} ${path[i].y} `;
        }
      });
      return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 600 200" width="600" height="200">
          <path d="${svgPathData}" stroke="black" fill="none" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" />
        </svg>`.trim();
    }
  }));

  const getCoordinates = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const clientX = (e.touches && e.touches[0]) ? e.touches[0].clientX : e.clientX;
    const clientY = (e.touches && e.touches[0]) ? e.touches[0].clientY : e.clientY;
    return {
      offsetX: (clientX - rect.left) * scaleX,
      offsetY: (clientY - rect.top) * scaleY
    };
  };

  const startDrawing = (e) => {
    if (e.type === 'touchstart') e.preventDefault();
    const { offsetX, offsetY } = getCoordinates(e);
    setIsDrawing(true);
    setPaths(prev => [...prev, [{ x: offsetX, y: offsetY }]]);
    const ctx = canvasRef.current.getContext('2d');
    ctx.beginPath();
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#101828';
    ctx.moveTo(offsetX, offsetY);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    if (e.type === 'touchmove') e.preventDefault();
    const { offsetX, offsetY } = getCoordinates(e);
    const ctx = canvasRef.current.getContext('2d');
    ctx.lineTo(offsetX, offsetY);
    ctx.stroke();
    setPaths(prev => {
      const newPaths = [...prev];
      newPaths[newPaths.length - 1].push({ x: offsetX, y: offsetY });
      return newPaths;
    });
  };

  const endDrawing = () => {
    if (isDrawing) {
      setIsDrawing(false);
      if (onStrokeEnd) onStrokeEnd();
    }
  };

  return (
    <canvas
      ref={canvasRef}
      width={600}
      height={200}
      onMouseDown={startDrawing}
      onMouseMove={draw}
      onMouseUp={endDrawing}
      onMouseOut={endDrawing}
      onTouchStart={startDrawing}
      onTouchMove={draw}
      onTouchEnd={endDrawing}
      style={{ 
        display: 'block', 
        width: '100%', 
        height: '200px', 
        cursor: 'crosshair', 
        touchAction: 'none',
        backgroundColor: '#fff' 
      }}
    />
  );
});

const SvgSignatureField = ({ value, onChange, label = "Signature" }) => {
  const canvasRef = useRef();

  const handleClear = () => {
    if (canvasRef.current) {
      canvasRef.current.clear();
      onChange(null);
    }
  };

  const handleEndDrawing = () => {
    if (canvasRef.current) {
      const svg = canvasRef.current.getSvg();
      onChange(svg);
    }
  };

  return (
    <Box sx={{ width: '100%', mt: 1 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="caption" fontWeight={700} color="#344054" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <GestureOutlined sx={{ fontSize: 16 }} /> {label.toUpperCase()}
        </Typography>
        {value && (
          <Button 
            size="small" 
            color="error" 
            onClick={handleClear} 
            startIcon={<DeleteOutline />}
            sx={{ textTransform: 'none', fontWeight: 600, py: 0 }}
          >
            Clear
          </Button>
        )}
      </Stack>

      <Box 
        sx={{ 
          border: '2px solid #EAECF0', 
          borderRadius: '12px', 
          bgcolor: '#fff', 
          overflow: 'hidden',
          transition: 'border-color 0.2s',
          '&:hover': { borderColor: '#D0D5DD' }
        }}
      >
        <SignatureCanvas ref={canvasRef} onStrokeEnd={handleEndDrawing} />
      </Box>
      
      <Typography variant="caption" color="#667085" sx={{ mt: 0.5, display: 'block', fontStyle: 'italic' }}>
        Please sign clearly within the border.
      </Typography>
    </Box>
  );
};

export default SvgSignatureField;